import React, { useContext, useEffect, useState } from 'react';
import Calendar from 'react-calendar';
import DownArrow from '@/assets/img/down-arrow.svg';

import { CalendarUsers } from '../../Calendar/types/index';
import { Room } from '@/features/rooms';
import { UserDetails } from '@/components/UserDetails';
import { Heading } from '@/components/Elements/Heading';
import { Business } from '@/features/communication';
import Utils from '@/utils/util';
import { ConfigureBusinessHours } from '@/features/business/routes/ConfigureBusinessHours';
import { Tabs } from '@/components/Elements/Tab/Tab';
import ListView from '@/assets/img/list-view.svg';
import { ReactComponent as SearchIcon } from '@/assets/img/search-iocn-v2.svg';
import { ReactComponent as CloseIcon } from '@/assets/img/Close-new.svg';

import CalendarViewSelected from '@/assets/img/calendar-view-selected.svg';
import { Dropdown } from 'react-bootstrap';
import moment from 'moment';
import { useGetBookedDateForRooms } from '@/features/rooms/api/getRoomsList';
import { useAuth } from '@/lib/auth';
import { UserContext } from '@/routes';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';

export type SlotsProps = {
  calenderUsers: CalendarUsers[] | undefined;
  selectedDate: Date | null;
  selectedRange: any;
  onScheduleDateChange: (item: any) => void;
  setCalenderUsers: (item: any) => void;
  onServiceProviderChange: (item: any) => void;
  selectedUser: CalendarUsers | undefined;
  isRoomService: boolean;
  isBoardingService: boolean;
  rooms?: Room[];
  onSelectRoom: (room: Room) => void;
  businessData?: Business;
  selectedBookingView: string;
  onBookingsViewChange: (id: string) => void;
  onGetBookingPrice: (data: any) => void;
  room?: Room;
};

export const ScheduleView = ({
  calenderUsers,
  selectedDate,
  onScheduleDateChange,
  setCalenderUsers,
  onServiceProviderChange,
  selectedUser,
  businessData,
  isRoomService,
  rooms,
  onSelectRoom,
  selectedBookingView,
  onBookingsViewChange,
  isBoardingService,
  onGetBookingPrice,
  selectedRange,
  room,
}: SlotsProps) => {
  const { user } = useAuth();
  const userContext = useContext(UserContext);
  const { t, i18n } = useTranslation();

  const [canShowCalender, setCanShowCalender] = useState(false);
  const [todayTz, setTodayTz] = useState(new Date());
  const [selectedRoom, setSelectedRoom] = useState<Room>();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredRooms, setFilteredRooms] = useState<Room[]>([]);
  const [dayRange, setDayRange] = useState<[Date, Date]>();
  const [alreadyBookedDates, setAlreadyBookedDates] = useState<any>([]);
  const [holidays, setHolidays] = useState<any>([]);

  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const bookedRoomsMutation = useGetBookedDateForRooms();
  const [bookingViews, setBookingViews] = useState<any>([]);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [activeStartDate, setActiveStartDate] = useState<string>('');
  useEffect(() => {
    const views = [];
    if (user?.offerings?.grooming) {
      views.push({
        label: t('Grooming'),
        show: user?.offerings?.grooming,
        icon: '',
        selectedIcon: ListView,
        id: 'grooming',
      });
    }
    if (user?.offerings?.daycare) {
      views.push({
        label: t('Daycare'),
        show: user?.offerings?.daycare,
        icon: '',
        selectedIcon: CalendarViewSelected,
        id: 'daycare',
      });
    }
    if (user?.offerings?.boarding) {
      views.push({
        label: t('Boarding'),
        show: user?.offerings?.boarding,
        icon: '',
        selectedIcon: CalendarViewSelected,
        id: 'boarding',
      });
    }
    setBookingViews(views);
  }, [t]);

  useEffect(() => {
    if (selectedBookingView) {
      const filteredIndex = bookingViews.findIndex((bv: any) => bv.id === selectedBookingView);
      setSelectedTabIndex(filteredIndex);
    }
  }, [selectedBookingView]);

  useEffect(() => {
    if (businessData) {
      setTodayTz(Utils.fromServerTz(new Date(), businessData?.timezone));
    }
  }, [businessData]);

  useEffect(() => {
    if (!room) {
      setSelectedRoom(undefined);
      setDayRange(undefined);
    }
  }, [room]);

  useEffect(() => {
    if (rooms && rooms.length) {
      if (!selectedRoom) {
        onRoomSelect(rooms[0]);
      }
    } else {
      setSelectedRoom(undefined);
    }
    filterRooms();
  }, [rooms]);

  useEffect(() => {
    if (selectedRoom && isBoardingService) {
      const startDate = activeStartDate ? activeStartDate : moment().format('YYYY-MM-DD');
      getDateAvailabilityForRooms(startDate, Number(selectedRoom.id));
    }
  }, [selectedRoom]);

  useEffect(() => {
    filterRooms();
  }, [searchTerm]);

  useEffect(() => {
    if (dayRange) {
      getServicePrice(dayRange);
    }
  }, [selectedRoom]);

  useEffect(() => {
    if (selectedRange && selectedRange.startDate && selectedRange.endDate) {
      setDayRange([new Date(selectedRange.startDate), new Date(selectedRange.endDate)]);
    } else {
      setDayRange(undefined);
    }
  }, [selectedRange]);

  type UserListProps = {
    users: any | undefined;
    showSearch: boolean;
    onHideSearch: () => void;
  };

  const UserList: React.FC<UserListProps> = ({ users, showSearch, onHideSearch }) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const filteredUsers =
      users &&
      users.length > 0 &&
      users.filter((user: any) => {
        if (selectedUser && user.id !== selectedUser?.id) {
          return user.name.toLowerCase().includes(searchTerm.toLowerCase());
        }
        return true;
      });

    const selectServiceProvider = (item: CalendarUsers) => {
      if (selectedUser && selectedUser.id !== item.id) {
        const tmp = users.map((el: any) =>
          el.id == item.id ? { ...el, selected: !item.selected } : { ...el, selected: false }
        );
        onServiceProviderChange(item.selected ? undefined : item);
        setCalenderUsers(tmp);
      }
    };

    return (
      <div className="user-list-calender">
        {showSearch && (
          <div className="relative w-full calender-top">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <SearchIcon />
            </div>
            <CloseIcon
              className="close-icon"
              onClick={() => {
                setSearchTerm('');
                onHideSearch();
              }}
            />
            <input
              type="text"
              id="simple-search"
              className="new-search-input  text-sm rounded-lg  block w-full pl-10 p-2.5"
              placeholder={`${t('Search members')}`}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        )}
        <ul className="divide-y divide-gray-200 dark:divide-gray-700 pt-2">
          {filteredUsers &&
            filteredUsers.length > 0 &&
            filteredUsers.map((item: any) => (
              <li
                className={
                  item.id == selectedUser?.id
                    ? 'pb-3 pt-3 border-none border-item slected-item cursor-pointer'
                    : 'pb-3 pt-3 border-none border-item cursor-pointer'
                }
                onClick={() => {
                  selectServiceProvider(item);
                }}
              >
                <UserDetails user={item} showIdentity={item?.isAdmin} />
              </li>
            ))}
        </ul>
      </div>
    );
  };
  const handleSearchChange = async (e: any) => {
    setSearchTerm(e.target.value);
  };
  const onRoomSelect = (selected: Room) => {
    setDayRange(undefined);
    setSelectedRoom(selected);
    onSelectRoom(selected);
    setSearchTerm('');
  };
  const filterRooms = () => {
    if (rooms && rooms.length) {
      if (searchTerm) {
        const filteredList = rooms.filter(
          (room) => room.name.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0
        );
        setFilteredRooms(filteredList);
      } else {
        setFilteredRooms([...rooms]);
      }
    }
  };

  const getServicePrice = (dates: any) => {
    if (isBoardingService && dates) {
      const startDate = formatDate(dates[0]);
      const endDate = formatDate(dates[1]);
      onGetBookingPrice({ startDate: startDate, endDate: endDate });
      // if (selectedRoom) {
      //   getDateAvailabilityForRooms(Number(selectedRoom.id), dates[0], dates[1]);
      // }
    }
  };

  const formatDate = (date: Date) => {
    return moment(date).format('YYYY-MM-DD');
  };

  const onDateChange = (dates: Date | [Date, Date]) => {
    if (Array.isArray(dates)) {
      setDayRange(dates);

      getServicePrice(dates);
    }
  };

  const getDateAvailabilityForRooms = async (date: string, room_Id?: any) => {
    const startDate = moment(date).startOf('month').startOf('week').format('YYYY-MM-DD');
    const endDate = moment(date).endOf('month').endOf('week').format('YYYY-MM-DD');
    const res = await bookedRoomsMutation.mutateAsync({
      roomId: room_Id,
      startDate,
      endDate,
    });
    if (res) {
      setAlreadyBookedDates(res?.busyDays);
      setHolidays(res?.holidays);
      // if (res?.busyDays && res.busyDays.length > 0) {
      //   showNotification({
      //     type: 'info',
      //     title: 'Some of the dates are already booked, please select some different date range.',
      //   });
      // }
    }
  };

  const tileClassName = ({ date }: { date: Date }) => {
    const formattedDate = moment(date).format('YYYY-MM-DD');
    if (alreadyBookedDates.includes(formattedDate)) {
      return 'booked';
    }
    if (holidays.includes(formattedDate)) {
      return 'holidays';
    }
    return null;
  };
  const tileClassNameDisabled = ({ date }: { date: Date }) => {
    const formattedDate = moment(date).format('YYYY-MM-DD');
    if (alreadyBookedDates.includes(formattedDate)) {
      return 'booked';
    }
    return null;
  };
  const onTabChange = (index: number) => {
    const selectedTab = bookingViews[index];
    onBookingsViewChange(selectedTab.id);
  };
  return (
    <>
      <div className="caleder-group">
        <Heading
          size="lg"
          title={`${t('Schedule')}`}
          description={`${t('Select the date that suits for booking')}`}
          customeCss={'add-bg-color'}
          fontWeight="sm"
        />
        <div className="tab-container-bt">
          <Tabs
            key={'appt-view'}
            selectedTabIndex={selectedTabIndex}
            onChange={onTabChange}
            tabs={bookingViews}
            mode="tab"
            responsive={false}
            isroomBooking={true}
          />
        </div>
        <div className="column-content-scroller">
          {isBoardingService ? (
            <>
              <div className="event-calender">
                <div className="calender-outer calender-width">
                  <Calendar
                    onChange={onDateChange}
                    selectRange
                    minDate={todayTz}
                    value={dayRange ? dayRange : null}
                    tileClassName={tileClassName}
                    tileDisabled={tileClassNameDisabled}
                    onActiveStartDateChange={(data: any) => {
                      setActiveStartDate(data.activeStartDate);
                      getDateAvailabilityForRooms(
                        data.activeStartDate,
                        selectedRoom ? Number(selectedRoom.id) : null
                      );
                    }}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="event-calender">
              {(userContext?.isLandscape || canShowCalender) && (
                <div className="calender-outer calender-width">
                  <Calendar
                    onChange={(date: any) => {
                      setCanShowCalender(false);
                      onScheduleDateChange(date);
                    }}
                    minDate={todayTz}
                    value={selectedDate}
                    calendarClassName="petsked-rangepicker"
                    colorPrimary="#F85663"
                    colorPrimaryLight="#FBF1F1"
                  />
                </div>
              )}
              {!userContext?.isLandscape && !canShowCalender && (
                <>
                  <div className="switch-date">
                    <div className="switch-cal-title">{t('Select booking date')}</div>
                  </div>
                  <div className="calender-outer calender-width">
                    <Calendar
                      onChange={(date: any) => {
                        setCanShowCalender(false);
                        onScheduleDateChange(date);
                      }}
                      minDate={todayTz}
                      value={selectedDate}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {!userContext?.isLandscape ? (
            <div className="switch-calennder">
              <div className="switch-cal-title">
                {isRoomService || isBoardingService
                  ? t('Select room to reserve')
                  : t('Switch calendar')}
              </div>
            </div>
          ) : (
            <div className="switch-calennder">
              <div className="switch-cal-title">
                {isRoomService || isBoardingService
                  ? t('Select room to reserve')
                  : t('Switch calendar')}

                {!showSearch && (
                  <span className="search-enable" onClick={() => setShowSearch(!showSearch)}>
                    <SearchIcon />
                  </span>
                )}
              </div>
            </div>
          )}

          {isRoomService || isBoardingService ? (
            <div className="petsked-form">
              {filteredRooms && filteredRooms.length === 0 && (
                <div className="no-rooms-data">{'Rooms are not available'}</div>
              )}
              {filteredRooms && filteredRooms.length > 0 && (
                <Dropdown className="new-figma-drop-down drodpdwon-btn-pink">
                  <Dropdown.Toggle
                    variant="success"
                    className="drop-down-btn fix-height-54"
                    id="dropdown-basic"
                  >
                    {selectedRoom ? selectedRoom.name : t('Select Room')}
                    <img src={DownArrow} className="down-arrow" />
                  </Dropdown.Toggle>
                  <Dropdown.Menu style={{ width: '100%' }}>
                    <span className="search-container">
                      <input
                        type="text"
                        className="form-control mb-2"
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                      <SearchIcon />
                    </span>
                    <div className="scroller">
                      {filteredRooms.map((item: Room) => (
                        <Dropdown.Item
                          key={item.id}
                          onClick={() => onRoomSelect(item)}
                          className={
                            selectedRoom && selectedRoom.id === item.id ? t('selected') : ''
                          }
                        >
                          {item.name}
                        </Dropdown.Item>
                      ))}
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              )}
              {/* <ReactSelect
              classNamePrefix={'petsked-multiselect'}
              className="multiselect-react"
              isMulti={false}
              options={rooms ? rooms : []}
              getOptionLabel={(option) => option.name}
              onChange={(selected: any) => {
                setSelectedRoom(selected);
                onSelectRoom(selected);
              }}
              components={{
                Option: BasicOption,
              }}
              value={selectedRoom && rooms?.find((bl: any) => bl.id == selectedRoom.id)}
              placeholder="Select room"
            /> */}
              {!isBoardingService && (
                <>
                  <p className="room-desc">{selectedRoom?.description}</p>
                  {selectedRoom && (
                    <ConfigureBusinessHours hours={selectedRoom.hours} readOnly={true} />
                  )}
                </>
              )}
            </div>
          ) : (
            <UserList
              users={calenderUsers}
              showSearch={showSearch}
              onHideSearch={() => setShowSearch(false)}
            />
          )}
        </div>
      </div>
    </>
  );
};
// const BasicOption = (props: any) => {
//   return (
//     <div>
//       <components.Option {...props}>
//         <div className="option-label">{props.label}</div>
//       </components.Option>
//     </div>
//   );
// };
